import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, switchMap, tap } from 'rxjs';

import { AuthenticationService } from 'app/core/authentication';
import { ITokenResponse } from 'app/core/authentication/models/token-response';
import { LoginService } from 'app/core/authentication/services/login.service';
import { IPartner, IPartnerParams } from 'app/shared/models/partner-activity.model';
import { SessionSettingsService } from 'app/shared/services/session-settings.service';
import { PageModeEnum } from 'app/teacher/activities/custom/page-mode.enum';
import { ClassInfo } from 'app/teacher/models/class-info';
import { TEACHER_ROUTE } from 'app/teacher/routing-constants/teacher-routing.constant';
import { TeacherDataService } from 'app/teacher/services/teacher-data.service';

import { PartnerActivityApiService } from './partner-activity-api.service';

@Injectable({
  providedIn: 'root'
})
export class PartnerActivityService {
  private router = inject(Router);
  private sessionSettingsService = inject(SessionSettingsService);
  private authService = inject(AuthenticationService);
  private readonly loginService = inject(LoginService);
  private readonly teacherDataService = inject(TeacherDataService);
  private apiService = inject(PartnerActivityApiService);

  private partners = ['Mathology', 'MC'];
  private supportedPartners: { [key: string]: IPartner } = {};

  public constructor() {
    // Initialize supported partners
    this.partners.forEach((partner) => {
      this.addSupportedPartner(partner);
    });
  }

  private addSupportedPartner(partnerId: string): void {
    this.supportedPartners[partnerId] = {
      initializePartner: (stateParams: IPartnerParams) => this.initializePartner(stateParams),
      signInAsGuest: () => this.signInAsGuest(partnerId),
      redirectToStartPage: (ClassId?: string) => this.redirectToStartPage(partnerId, ClassId),
      Params: {} as IPartnerParams // Initialize Params as an empty object
    };
  }

  // Initializes the partner using the URL parameters passed in stateParams
  public initializePartner(stateParams: IPartnerParams): boolean {
    if (stateParams.PublicUnitId && stateParams.CourseId && stateParams.CountryCode && stateParams.RegionCode) {
      this.supportedPartners[stateParams.PartnerId].Params = {
        PublicUnitId: stateParams.PublicUnitId,
        CourseId: stateParams.CourseId,
        CountryCode: stateParams.CountryCode,
        RegionCode: stateParams.RegionCode,
        ClassId: null,
        PartnerId: stateParams.PartnerId
      };
      return true;
    }
    return false;
  }

  // Saves partner data to session settings for later use
  private savePartnerData(PartnerId: string, data: Partial<IPartnerParams>): void {
    const partnerData: { [key: string]: Partial<IPartnerParams> } = { };
    partnerData[PartnerId] = data;
    this.sessionSettingsService.set('PartnerData', partnerData);
  }

  // Signs in as a guest or registers based on partner and user state
  public signInAsGuest(partner: string): Observable<ClassInfo[] | ITokenResponse> {
    const partnerData = this.supportedPartners[partner].Params;

    // Handle guest sign-in if the user is transient
    if (this.authService.isTransient()) {
      this.savePartnerData(partner, { PublicUnitId: partnerData.PublicUnitId });
      return this.teacherDataService.getTeacherClasses().pipe(
        tap((classInfo: ClassInfo[]) => {
          if (classInfo.length) {
            partnerData.ClassId = classInfo[0].ClassId;
          }
        })
      );
    }
    // Otherwise, register the partner and get tokens

    return this.apiService.partnerTokenResponse(partnerData, partner).pipe(
      tap((tokenResponse: ITokenResponse) => {
        this.authService.setUserClaims(this.loginService.convertToUserClaims(tokenResponse));
        this.authService.setTokens(this.loginService.convertToToken(tokenResponse));
      }),
      switchMap(() => this.teacherDataService.getTeacherClasses().pipe(
        tap((classInfo: ClassInfo[]) => {
          partnerData.ClassId = classInfo[0].ClassId;
          this.savePartnerData(partner, { PublicUnitId: partnerData.PublicUnitId });
        })
      ))
    );
  }

  // Redirects the user to the start page based on the partner's data
  public redirectToStartPage(partner: string, ClassId?: string): void {
    const partnerParams = this.supportedPartners[partner].Params;
    // Use the ClassId passed from the partnerParams, or fallback to the one in component
    const classId = partnerParams.ClassId || ClassId;
    const publicUnitId = partnerParams.PublicUnitId;

    // If classId is still not defined, handle appropriately
    if (!classId) {
      console.error('ClassId is missing, unable to redirect');
      return;
    }
    const contentRoute = TEACHER_ROUTE.CONTENT.VIEW.route(classId, publicUnitId, PageModeEnum.Default);
    this.router.navigate(contentRoute).catch((error: unknown) => console.error(error));
  }

  // Returns the partner's data from the supported partners
  public getPartner(PartnerId: string): IPartner {
    return this.supportedPartners[PartnerId];
  }

  // Loads previously saved partner data from session settings
  public loadPartnerData(): Record<string, any> {
    return this.sessionSettingsService.get('PartnerData') || { PartnerId: null };
  }

  // Checks if the user is a Mathology user based on the partner data
  public isMathologyUser(): boolean {
    const keys = Object.keys(this.supportedPartners);
    const id = this.loadPartnerData().PartnerId;
    return keys.includes(id);
  }

  // Checks if the user is a partner user based on saved data
  public isPartnerUser(): boolean {
    return !!this.loadPartnerData().PartnerId;
  }
}
